import { IEntry } from "core-integration/src/store/entries/types";
import { IEvent } from "core-integration/src/store/events/types";
import { shuffle } from "lodash";
import { WebViewContext } from "plos/src/contexts/WebViewContext";
import useIsMobile from "plos/src/hooks/useIsMobile";
import { useLanguageFromPath } from "plos/src/hooks/useLanguageFromPath";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { loadAdobeExpressSDK } from "../utils/adobe";

interface CommunityWallAssetData {
  assetId: string; // Asset ID for the community asset
  title: string; // Prompt for the thumbnail item
  thumbnailSrc: string; // Source of the thumbnail image as a base64 string
  fullRenditionSrc: string; // Source of the full rendition image for OneUp view as a base64 string
  height: number; // Height of the thumbnail image
  width: number; // Width of the thumbnail image
  ownerInfo?: {
    // Optional: Information about the owner
    name: string; // Owner's display name
    imgSrc: string; // Source URL of the owner's image
  };
}

interface CommunityWallAssetResponse {
  assets: CommunityWallAssetData[];
  cursor: string; // The cursor for the next page of assets.
}

interface IEntryImage {
  asset_id: string;
  prompt: string;
  image_url: string;
}

enum ModuleIntent {
  EDIT_IMAGE = "edit-image",
  EDIT_IMAGE_V2 = "edit-image-v2",
  CREATE_IMAGE_FROM_TEXT = "create-image-from-text",
  CREATE_TEMPLATE_FROM_TEXT = "create-template-from-text",
  START_FROM_CONTENT = "start-from-content",
  VIEW_DESIGN = "view-design",
}

export interface ICreateImageArgs {
  badgeHolder: boolean;
  onImageCreate: (img: Blob, prompt: string) => Promise<void>;
  onImageCreateError?: (error: any) => void;
  onCancel?: () => void;
}

export type IEntryPoint =
  "deep-link" | "in-app-modal" | "persistent-cta" | "push-notification";

export type ITemplateType = "gameweek" | "season";

export interface IViewImageArgs {
  badgeHolder: boolean;
  entryPoint?: IEntryPoint;
  onImageShareError?: () => void;
  onImageShareSuccess?: () => void;
  url: string;
  type?: ITemplateType;
}

export interface IGetTemplateArgs {
  baseUrl: string;
  entry: IEntry;
  event: IEvent;
  type?: ITemplateType;
}

interface IAdobeContext {
  embed: {
    createImage: (args: ICreateImageArgs) => Promise<void>;
    viewImage: (args: IViewImageArgs) => void;
  };
  enabled: boolean;
  getTemplate: (args: IGetTemplateArgs) => Promise<string | undefined>;
  ready: boolean;
  setEnabled: (value: boolean) => void;
}

const AdobeContext = createContext<IAdobeContext | null>(null);

export default AdobeContext;

interface IAdobeProviderProps {
  children: React.ReactNode;
}

export const AdobeProvider: React.FC<IAdobeProviderProps> = ({ children }) => {
  const adobeRef = useRef<any | null>(null);
  const [enabled, setEnabled] = useState<boolean>(true);
  const [ready, setReady] = useState<boolean>(false);

  const { isWebView } = useContext(WebViewContext);
  const language = useLanguageFromPath();

  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();

  const platform = (() => {
    const platform = searchParams.get("platform")?.toLowerCase();
    if (platform) {
      if (["android", "ios"].includes(platform)) {
        return platform;
      } else {
        return null;
      }
    } else {
      if (isMobile) {
        return "mobile-web";
      } else {
        return "desktop-web";
      }
    }
  })();

  // Mobile Switch
  const mobileEnabled = true;

  useEffect(() => {
    (async () => {
      try {
        // If mobile is turned off and we detect a mobile device, set enabled to false
        if (!mobileEnabled && isMobile) {
          setEnabled(false);
        } else {
          await loadAdobeExpressSDK(language, mobileEnabled);
          if (window.adobeModule) {
            adobeRef.current = window.adobeModule;
          }
        }
      } catch (e) {
        setEnabled(false);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  let communityWallImageCount = 0;

  async function myFetchCommunityAssets(): Promise<CommunityWallAssetResponse> {
    const res = await fetch("/api/community-wall/");
    let data = [];
    if (res.ok) {
      data = await res.json();
    }

    let nextCursor = "Interim_Page";
    const displayLimit = 16;
    const assets = shuffle(data).map((i: IEntryImage) => {
      return {
        assetId: i.asset_id,
        title: i.prompt,
        thumbnailSrc: i.image_url,
        fullRenditionSrc: i.image_url,
        height: 50,
        width: 50,
      };
    });

    communityWallImageCount += assets.length;
    if (
      communityWallImageCount < displayLimit ||
      communityWallImageCount >= displayLimit
    ) {
      nextCursor = "Last_Page";
    }

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ assets: assets.slice(0, displayLimit), cursor: nextCursor });
      }, 1000);
    });
  }

  const createImage = async ({
    badgeHolder,
    onImageCreate,
    onImageCreateError,
    onCancel,
  }: ICreateImageArgs) => {
    if (!adobeRef.current) return;
    let imagePrompt = "";

    const isPromptSafe = (prompt: string) => {
      imagePrompt = prompt;
      return new Promise((resolve) => {
        resolve({ isSafe: true });
      });
    };

    const containerConfig = {
      zIndex: 610,
    };

    const exportConfig = [
      {
        action: {
          target: "publish",
          outputType: "blob",
          closeTargetOnExport: false,
          publishFileType: "image/png",
        },
        id: "saveToHostApp",
        label: "Submit",
        style: { uiType: "button" },
      },
    ];

    const handleOnPublish = async (_: ModuleIntent, publishParams: any) => {
      try {
        await onImageCreate(
          publishParams.asset[0].data,
          imagePrompt ? imagePrompt : publishParams.prompt,
        );
        return { status: "SUCCESS" };
      } catch (err) {
        console.error("Badge save failed", err);
        return { status: "DENIED" };
      }
    };

    const appConfig = {
      appVersion: "2",
      callbacks: {
        onCancel: () => {
          if (onCancel) {
            onCancel();
          }
        },
        onError: (error: any) => {
          if (onImageCreateError) {
            onImageCreateError(error);
          }
        },
        onIntentChange: (_: ModuleIntent, __: ModuleIntent) => {
          // Intent changes from ModuleIntent.CREATE_IMAGE_FROM_TEXT to ModuleIntent.EDIT_IMAGE
          return {
            appConfig: {
              callbacks: {
                onCancel: () => {
                  if (onCancel) {
                    onCancel();
                  }
                },
                onError: (error: any) => {
                  if (onImageCreateError) {
                    onImageCreateError(error);
                  }
                },
                onPublish: handleOnPublish,
              },
              metaData: {
                badgeHolder: badgeHolder ? "yes" : "no",
                ...(platform ? { platform } : {}),
              },
              useClientAuth: true,
            },
            containerConfig,
            exportConfig,
          };
        },
        onPublish: handleOnPublish,
      },
      communityWallConfig: {
        fetchCommunityAssets: myFetchCommunityAssets,
      },
      editDropdownOptions: [
        { option: "apply-adjustment" },
        { option: "add-effects" },
        { option: "remove-background" },
      ],
      featureConfig: {
        "community-wall": true,
      },
      imageDimensions: {
        size: { width: 500, height: 500, unit: "px" },
        aspectRatio: "square",
      },
      isPromptSafe: (prompt: string) => isPromptSafe(prompt),
      panelSettings: {
        contentType: { value: "auto" },
      },
      metaData: {
        badgeHolder: badgeHolder ? "yes" : "no",
        ...(platform ? { platform } : {}),
      },
      promptInputPlaceholder:
        "Describe the badge you want to generate – e.g. 'A lion on a red shield football team badge'",
      useClientAuth: true,
    };

    await adobeRef.current.createImageFromText(
      appConfig,
      exportConfig,
      containerConfig,
    );
  };

  const viewImage = async ({
    badgeHolder,
    entryPoint,
    onImageShareError,
    onImageShareSuccess,
    url,
    type = "gameweek",
  }: IViewImageArgs) => {
    if (!adobeRef.current) return;
    const docConfig = {
      asset: {
        type: "image",
        name: "test",
        dataType: "url",
        data: url,
      },
    };

    const handleShare = async (imageUrl: string) => {
      if (!navigator.share) {
        onImageShareError?.();
        return;
      }

      try {
        // Fetch image using imageUrl
        const response = await fetch(imageUrl);
        const blob = await response.blob();

        // Create a File object
        const file = new File(
          [blob],
          type === "season" ? "season_recap.jpg" : "gameweek_recap.jpg",
          {
            type: blob.type || "image/jpeg",
          },
        );

        const shareData = {
          files: [file],
          title: type === "season" ? "Season Recap" : "Gameweek Recap",
        };

        // Check if file sharing is supported
        if (navigator.canShare && !navigator.canShare(shareData)) {
          onImageShareError?.();
        } else {
          await navigator.share(shareData).then(() => {
            onImageShareSuccess?.();
          });
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        onImageShareError?.();
      }
    };

    const appConfig = {
      analyticsData: {
        entryPoint,
      },
      callbacks: {
        onPublish: async (_: ModuleIntent, publishParams: any) =>
          await handleShare(publishParams.asset[0].data),
      },
      designTitle: type === "season" ? "My Season Recap" : "My Gameweek Recap",
      metaData: {
        badgeHolder,
        ...(platform ? { platform } : {}),
      },
      previewThumbnails: {
        collectionConfig: {
          collectionId:
            "urn:aaid:sc:VA6C2:35e85094-7807-45aa-abac-c9aeac11eb2e",
          count: 7,
        },
      },
      useClientAuth: true,
    };

    const exportConfig: any[] = [];

    const containerConfig = {
      hideCloseButton: isWebView ? true : false,
      zIndex: 610,
    };

    await adobeRef.current.viewDesign(
      docConfig,
      appConfig,
      exportConfig,
      containerConfig,
    );
  };

  const getTemplate = async ({
    baseUrl,
    entry,
    event,
    type = "gameweek",
  }: IGetTemplateArgs) => {
    let templateUrl;

    if (type == "gameweek") {
      templateUrl = `${baseUrl}imggen/${event.id}/${entry.id}.jpg`;
    } else if (type == "season") {
      templateUrl = `${baseUrl}imggen/season/${event.id}/${entry.id}.jpg`;
    }
    // Uncomment the below to enable the Gameweek Summary locally
    // templateUrl = "https://plfpl-testing-next.staging.ismgames.com/gcs/ismfg-staging-static-content/plfpl-testing-next/imggen/6/1.jpg";

    if (!templateUrl) return;

    try {
      const response = await fetch(templateUrl, { method: "HEAD" });
      if (response.ok) {
        return templateUrl;
      }
    } catch {
      // Template not available, don't show button
      return;
    }
  };

  return (
    <AdobeContext.Provider
      value={{
        embed: {
          createImage,
          viewImage,
        },
        enabled,
        getTemplate,
        ready,
        setEnabled,
      }}
    >
      {children}
    </AdobeContext.Provider>
  );
};

export const useAdobeContext = () => {
  const context = useContext(AdobeContext);
  if (!context) {
    throw new Error("useAdobeContext must be used within an AdobeProvider");
  }
  return context;
};
